import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Box,
  Divider,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import HomeIcon from "@mui/icons-material/Home";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DnsIcon from "@mui/icons-material/Dns";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import RadarIcon from "@mui/icons-material/Radar";
import DescriptionIcon from "@mui/icons-material/Description";
import SettingsIcon from "@mui/icons-material/Settings";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import api from "lib/axios";
import { BRAND } from "assets/theme/brand";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

const UserTopNavBar = () => {
  const { auth } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleLogout = async () => {
    setAnchorEl(null);
    try {
      await api.post("/logout");
    } catch (_) {}
    try {
      localStorage.removeItem("accessToken");
      localStorage.removeItem("role");
    } catch {}
    window.location.replace("/portal/authentication/sign-in");
  };

  const menuItems = [
    { label: "Overview", path: "/dashboard", icon: <HomeIcon /> },
    { label: "Risks", path: "/risks", icon: <WarningAmberIcon /> },
    { label: "Assets", path: "/assets", icon: <DnsIcon /> },
    { label: "Credentials", path: "/credentials", icon: <VpnKeyIcon /> },
    { label: "Monitoring", path: "/monitoring", icon: <RadarIcon /> },
    { label: "Reports", path: "/reports", icon: <DescriptionIcon /> },
    { label: "Settings", path: "/settings", icon: <SettingsIcon /> },
  ];

  const getPageTitle = () => {
    const currentPath = location.pathname.toLowerCase();
    const match = menuItems.find(
      (item) => currentPath === item.path || currentPath.startsWith(`${item.path}/`)
    );
    return match ? match.label : "Overview";
  };

  const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const open = Boolean(anchorEl);
  const id = open ? "nav-popover" : undefined;

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log("[UserTopNavBar] role?", {
      authRole: auth?.role,
      ls: localStorage.getItem("role"),
    });
  }, [auth?.role]);

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  return (
    <>
      {/* Transparent floating top bar (logo left, title center, burger right) */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          backgroundColor: "rgba(7,17,31,0.82)",
          px: 2,
          py: 1,
          boxShadow: "none",
          backdropFilter: "blur(18px)",
          borderBottom: `1px solid ${BRAND.line}`,
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between", minHeight: "64px" }}>
          <Box
            component="img"
            src={BRAND.logo}
            alt="NetDefenders"
            sx={{
              height: { xs: 30, md: 40 },
              width: "auto",
              maxWidth: { xs: 170, md: 230 },
              flexShrink: 0,
            }}
          />

          <Typography
            variant="h3"
            sx={{
              flexGrow: 1,
              textAlign: "center",
              fontWeight: 800,
              color: BRAND.text,
              letterSpacing: "-0.035em",
              userSelect: "none",
              position: "absolute",
              left: 0,
              right: 0,
              pointerEvents: "none",
            }}
          >
            {getPageTitle()}
          </Typography>

          <IconButton edge="end" onClick={handleMenuClick} sx={{ p: 1.5 }}>
            <MenuIcon sx={{ width: 40, height: 40, color: BRAND.accent }} />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Cream popover; dark-blue items; purple icons */}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            mt: 1,
            px: 1,
            py: 1,
            backgroundColor: "#f4f8fb",
            color: BRAND.bg,
            width: { xs: "60vw", sm: "260px" },
            maxHeight: { xs: "90vh", sm: "auto" },
            boxShadow: "0px 6px 24px rgba(0,0,0,0.18)",
            overflowY: "auto",
            border: "1px solid rgba(0,0,0,0.06)",
          },
        }}
      >
        <List dense disablePadding>
          {menuItems.map((item) => (
            <ListItem
              key={item.path}
              onClick={() => {
                navigate(item.path);
                handleClose();
              }}
              sx={{
                borderRadius: 1.2,
                px: 2,
                py: 1.25,
                minHeight: 46,
                cursor: "pointer",
                "& .MuiListItemText-primary": {
                  fontSize: { xs: "1rem", sm: "0.92rem" },
                  fontWeight: 700,
                  color: BRAND.bg,
                },
                "&:hover": { backgroundColor: "rgba(77,225,193,0.12)" },
              }}
            >
              <ListItemIcon sx={{ color: BRAND.accent2, minWidth: 36 }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItem>
          ))}

          <Divider sx={{ my: 1, borderColor: "rgba(0,0,0,0.08)" }} />

          <ListItem
            onClick={handleLogout}
            sx={{
              borderRadius: 1.2,
              px: 2,
              py: 1.25,
              minHeight: 46,
              cursor: "pointer",
              "& .MuiListItemText-primary": {
                fontSize: { xs: "1rem", sm: "0.92rem" },
                fontWeight: 700,
                color: "#c62828",
              },
              "&:hover": { backgroundColor: "rgba(198,40,40,0.08)" },
            }}
          >
            <ListItemIcon sx={{ color: "#c62828", minWidth: 36 }}>
              <ExitToAppIcon />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItem>
        </List>
      </Popover>
    </>
  );
};

export default UserTopNavBar;
